import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { productClick } from 'analytics/enhanced-ecommerce'
import TrackingContext from 'contexts/trackingContext'
import Spacing from 'components/Common/Spacing'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel'
import PaneBody from 'components/Common/Pane/PaneBody'
import Pane from 'components/Common/Pane'
import Heading from 'components/Luxkit/Typography/Heading'
import { productClickObject } from 'analytics/eventDefinitions'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOffers from 'hooks/Offers/useOffers'
import StandaloneLocationSeededYmalListOffer, { CARD_WIDTH } from './StandaloneLocationSeededYmalListOffer'
import { getLereListSource } from '../common/constant'
import { useLereNearbyOffers } from 'hooks/Recommendation/useLereNearbyOffers'
import CarouselCardLoadingSkeleton from 'components/OfferList/OfferCards/CarouselCardLoadingSkeleton'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { without } from 'lib/array/arrayUtils'

const LoadingCard = styled(CarouselCardLoadingSkeleton)`
  width: 75vw;
  ${mediaQueryUp.tablet} {
    width: ${rem(CARD_WIDTH)};
  }
  min-height: ${rem(400)};
`

interface OwnProps {
  destinationId: string;
  excludeOfferIds: Array<string>;
}

interface Props extends OwnProps {
  analyticPageSource: string;
  currency: string;
}

function StandaloneLocationSeededYmalList(props: Props) {
  const { destinationId, excludeOfferIds, analyticPageSource, currency } = props

  const validDestination = destinationId.startsWith('le_')
  const lereNearbyOffers = useLereNearbyOffers(validDestination, destinationId)
  const offerIds = useMemo(() => {
    const lereIds = lereNearbyOffers?.offers?.map(offer => offer.offerId) ?? []
    return without(lereIds, ...excludeOfferIds)
  }, [lereNearbyOffers, excludeOfferIds])

  const [offers, offersFetching] = useOffers<App.Offer>(offerIds)

  const lereVersion = lereNearbyOffers?.lereVersion ?? 'unknown'
  const tracking = useMemo(() => {
    const listName = getListName('LERE', analyticPageSource, 'Location Seeded YMAL')
    const lere = { version: lereVersion }
    return ({
      label: listName,
      category: 'Recommendations',
      listId: uuidV4(),
      listName,
      lere,
    })
  }, [analyticPageSource, lereVersion])

  const onClick = useCallback((offer: App.Offer, position: number) => {
    productClick(currency, productClickObject(offer, position), {
      listId: tracking.listId,
      listName: tracking.listName,
      listSource: getLereListSource(tracking.lere?.version),
    })
  }, [currency, tracking])

  const isLoading = !lereNearbyOffers || lereNearbyOffers.state === 'loading' || offersFetching

  if (!validDestination || (!isLoading && offers.length === 0)) {
    return null
  }

  return (
    <TrackingContext.Provider value={tracking}>
      <Pane>
        <PaneBody size="xl-y">
          <Heading variant="heading3">You may also like</Heading>
          <Spacing mt={16}>
            <TrackedCardCarousel pageSize={4}>
              {isLoading &&
                <>
                  <LoadingCard />
                  <LoadingCard />
                  <LoadingCard />
                  <LoadingCard />
                </>
              }
              {offers.map((offer, index) => <StandaloneLocationSeededYmalListOffer key={offer.id} tracking={tracking} offer={offer} onClick={() => onClick(offer, index + 1)} position={index + 1}/>)}
            </TrackedCardCarousel>
          </Spacing>
        </PaneBody>
      </Pane>
    </TrackingContext.Provider>
  )
}

function mapStateToProps(state: App.State, ownProps: OwnProps) {
  return {
    currency: state.geo.currentCurrency,
    destinationId: ownProps.destinationId,
  }
}

export default connect(mapStateToProps)(StandaloneLocationSeededYmalList)
